import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api, { formatError } from "@/lib/api";

export default function DeleteAccount() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [submitted, setSubmitted] = useState(false);

    function update(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        if (!form.name.trim() || !form.email.trim()) {
            setError("Name and email are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/account-deletion-requests", {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                reason: form.reason.trim() || null,
            });
            setSubmitted(true);
        } catch (err) {
            setError(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="bg-white min-h-screen" data-testid="delete-account-page">
            <header className="border-b border-brand-line sticky top-0 bg-white/95 backdrop-blur z-10">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" data-testid="delete-account-home-link" className="flex items-center">
                        <Logo className="h-8" />
                    </Link>
                    <Link to="/" className="text-sm font-medium text-brand-action hover:underline">← Back to maintayn.in</Link>
                </div>
            </header>

            <main className="max-w-xl mx-auto px-6 py-12 sm:py-16">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-brand-ink mb-3" data-testid="delete-account-title">
                    Delete your account
                </h1>
                <p className="text-sm sm:text-base text-brand-inkSoft leading-relaxed mb-8">
                    If you can sign in, the fastest way is <strong>in the app</strong>: Profile → "Delete My Account".
                    Use this form only if you can't sign in, or prefer our support team to process it for you.
                    Requests are verified and processed within <strong>7 business days</strong>.
                </p>

                {submitted ? (
                    <div className="rounded-2xl border border-brand-line bg-brand-bg p-8 text-center" data-testid="delete-account-success">
                        <CheckCircle size={40} weight="fill" className="text-brand-action mx-auto mb-3" />
                        <h2 className="font-heading text-xl font-bold text-brand-ink mb-2">Request received</h2>
                        <p className="text-sm text-brand-inkSoft leading-relaxed">
                            We've emailed a confirmation to <strong>{form.email}</strong>. Our support team will verify
                            your identity and process the deletion within 7 business days. If we need anything else,
                            we'll reply on that email thread.
                        </p>
                        <Link to="/" className="inline-block mt-6 text-sm font-medium text-brand-action underline">
                            Back to maintayn.in
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="delete-account-form">
                        <div className="space-y-2">
                            <Label htmlFor="da-name">Full name *</Label>
                            <Input
                                id="da-name"
                                data-testid="delete-account-name-input"
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                placeholder="Your full name"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="da-email">Email address used on Maintayn *</Label>
                            <Input
                                id="da-email"
                                type="email"
                                data-testid="delete-account-email-input"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                placeholder="you@example.com"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="da-phone">Phone number (optional)</Label>
                            <Input
                                id="da-phone"
                                type="tel"
                                data-testid="delete-account-phone-input"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                placeholder="Helps us verify it's really you"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="da-reason">Reason (optional)</Label>
                            <Textarea
                                id="da-reason"
                                data-testid="delete-account-reason-input"
                                value={form.reason}
                                onChange={(e) => update("reason", e.target.value)}
                                placeholder="Tell us why you're leaving (optional)"
                                rows={4}
                            />
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="delete-account-error">
                                <WarningCircle size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="rounded-xl bg-brand-bg border border-brand-line p-4 text-xs sm:text-sm text-brand-inkSoft leading-relaxed">
                            Submitting this request permanently deletes or irreversibly anonymizes your personal data
                            — name, email, phone and photos — and disables your login. A narrow set of records tied
                            to your flat (financial, legal, or security/audit logs) may be retained in de-identified
                            form for your society's accounting and compliance needs. See our{" "}
                            <Link to="/privacy-policy#10" className="text-brand-action underline">Privacy Policy §10</Link> for full details.
                        </div>

                        <Button
                            type="submit"
                            data-testid="delete-account-submit-button"
                            disabled={submitting}
                            className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white py-6 text-base font-semibold"
                        >
                            <Trash size={18} className="mr-2" />
                            {submitting ? "Submitting…" : "Submit deletion request"}
                        </Button>
                    </form>
                )}

                <div className="mt-10 pt-6 border-t border-brand-line flex flex-wrap gap-4 text-sm">
                    <Link to="/" className="text-brand-action underline">maintayn.in</Link>
                    <Link to="/privacy-policy" className="text-brand-action underline">Privacy Policy</Link>
                    <a href="mailto:support@maintayn.in" className="text-brand-action underline">support@maintayn.in</a>
                </div>
            </main>
        </div>
    );
}
